import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { useUser } from '@clerk/react';
import log from 'loglevel';

import { useTeamHub } from '../../pages/private/Team/hooks/useTeamHub';
import {
  useFetchCacheStore,
  useInboxEntriesStore,
  useSlackCommunicationsStore,
  useUserCommunicationsStore,
} from '../../store';
import { indexedDBAdapter } from '../../store/utils/indexedDBAdapter';
import { useSmartDataFetch } from '../DataFetch';
import { useGetIntegrations } from '../Integrations';
import { useOnReconnect } from '../OnReconnect';

// StrictMode mounts the component twice in dev; this guard ensures init runs once per app load.
let didInit = false;

export const resetOnInitFlag = () => {
  didInit = false;
};

export const useOnInit = () => {
  const { isLoaded, isSignedIn } = useUser();
  const isAuthReady = isLoaded && isSignedIn;
  const { handleGetIntegrations } = useGetIntegrations();
  const { ensureTeamsLoaded, ensurePendingInvitesLoaded } = useTeamHub({
    autoLoad: false,
  });
  useOnReconnect();

  const { resetState: resetInboxEntries } = useInboxEntriesStore();
  const { resetState: resetUserCommunications } = useUserCommunicationsStore();
  const { resetState: resetSlackCommunications } =
    useSlackCommunicationsStore();
  const { clearCache } = useFetchCacheStore();
  const { fetchInitialData } = useSmartDataFetch();
  const location = useLocation();

  useEffect(() => {
    indexedDBAdapter
      .clearExpiredContactCache()
      .catch((err) => log.error('Failed to clear expired contact cache', err));
  }, []);

  useEffect(() => {
    if (didInit || !isAuthReady) return;
    didInit = true;

    const initializeApp = async () => {
      log.info('[useOnInit] Resetting all states before initialization');

      resetInboxEntries();
      resetUserCommunications();
      resetSlackCommunications();
      clearCache();

      try {
        await Promise.all([
          handleGetIntegrations().then(() =>
            fetchInitialData(location.pathname),
          ),
          ensureTeamsLoaded(),
          ensurePendingInvitesLoaded(),
        ]);
      } catch (error) {
        log.error('Error during initialization:', error);
      }
    };

    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- guarded by didInit; runs exactly once once Clerk is ready
  }, [isAuthReady]);
};
